import Axios, { isAxiosError } from 'axios';
import { v4 as guid } from 'uuid';
import config from '@platform/shared/config';
import { saveAs } from 'file-saver';
import * as extFunc from '@platform/components/ext_function';
import { UserStore } from '@platform/store';
import { normalize } from '@platform/shared/helpers/formatters';
import { NodeAPI, sqlFilter, sqlFilterAnyProperty } from '@platform/shared/rpc';
import { EmployeeEntity } from '@platform/entities/employee/employee.entity';
import { EmployeeProjectEntity } from '@platform/entities/employee_project/employee_project.entity';
import { doRpcLegacy } from '@platform/shared/rpc/node/rpc-legacy';
import { NAME as statuses } from '@platform/components/docflow/statuses';
import { default as platformAxios } from '@platform/shared/rpc/node/axios.instance';
import { Notify } from 'quasar';

const options = {
    headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'Filestore-Api-Key': config.apiKeyFileStore,
    },
};
const axios = Axios.create(options);
class RpcError extends Error {
    success = false;
    axiosError = null;

    constructor(message, axiosError) {
        super(message);
        this.axiosError = axiosError;
        this.code = axiosError?.code;
        this.axiosConfig = axiosError?.config;
    }
}

export function setRpcAuthData(token) {
    axios.defaults.headers.Authorization = token ? `Bearer ${token}` : undefined;
}

export function addOnRpcErrorListener(listener) {
    axios.interceptors.response.use(
        function (response) {
            return response;
        },
        function (err) {
            if (!err?.response?.config?.url?.endsWith('login')) listener(err.message, err);
            return Promise.reject(err);
        }
    );
}

addOnRpcErrorListener((type, error) => {
    if (error && isAxiosError(error) && error.config?.url?.endsWith('pusk/login')) {
        return;
    }
    Notify.create({
        message:
            type === 'NetworkError'
                ? 'Отсутствует соединение с сервером. Проверьте ваше подключение к Интернету и попробуйте повторить операцию'
                : 'Произошла ошибка на сервере\n' + String(error?.response?.data?.message || '').substring(0, 80),
        position: 'top-right',
        icon: 'report_problem',
        color: 'negative',
        timeout: 5000,
        multiLine: true,
        actions: [
            {
                label: 'ОК',
                color: 'white',
            },
        ],
    });
});

export async function do_rpc(type, data) {
    let requestData;
    data.sysParams = UserStore.userState.sysParams;
    if (type === 'read') console.debug('read', data);
    await doRpcLegacy(type, data)
        .then((r) => {
            if (typeof r === 'number' && isNaN(r)) {
                return axios.post(`${config.api_url}/${type}`, data);
            }
            return { data: r, status: 200 };
        })
        .then((response) => {
            requestData = response.data;
            if (!!response && response.status === 200 && (!!response.data.success || type === 'login')) {
                if (type === 'read') {
                    console.debug(
                        'do_rpc - OK 200: ' +
                            type +
                            ', from table: ' +
                            (data.tableName ? data.tableName : '---') +
                            ', items: ' +
                            (requestData.data ? requestData.data.length : 0)
                    );
                } else {
                    console.debug(
                        'do_rpc - OK 200: ' +
                            type +
                            ', from table: ' +
                            (data.tableName ? data.tableName : '---') +
                            ', result: ' +
                            (requestData.success ? 'SUCCESS' : 'ERROR')
                    );
                }
            } else {
                console.error(
                    'do_rpc - NOT OK: ' +
                        type +
                        ', from table: ' +
                        (data.tableName ? data.tableName : '---') +
                        ', response.status: ' +
                        response.status
                );
                console.debug(response);
                throw new Error('Error');
            }
            return requestData;
        })
        .catch((error) => {
            console.error('do_rpc - ERR: ' + type + ', from table: ' + (data.tableName ? data.tableName : '---'));
            return Promise.reject(
                new RpcError(
                    'do_rpc - ERR: ' + type + ', from table: ' + (data.tableName ? data.tableName : '---'),
                    error
                )
            );
        });

    return requestData;
}

/**
// type:
//    list - чтение списка
//    read - чтение 1-ой записи
//    read_template - чтение 1-ой записи (sys_row_status = 'TMP')
//    modify - изменение записей
//    savedraft - запись черновика (sys_row_status = 'DRF')
//    save_template - запись шаблона (sys_row_status = 'TMP')
//    delete - удаление записи (пометка на удаленеи sys_row_status = 'DEL')
//    restore - восстановление записи (sys_row_status = 'ACT')
// table - наименование таблицы
// params - параметры запроса
 */
/**
 *
 * @param type {'list' | 'read' | 'read_template' | 'modify' | 'savedraft' | 'save_template' | 'delete' | 'restore' | 'read_structure' | 'login' | 'read_any' | string }
 * @param table
 * @param params {{sqlFilter?: RpcFilters | string | null; tableStructure?: any[]; _legacyJson?: boolean; page?: number | [page: number, pageSize?: number]} | any[] |  null}
 * @param funcGetDataStructure {(() => Record<string, any>) | null | undefined}
 * @param funcGetGridApi {GridApi | null | undefined}
 * @param schemaName {string | null | undefined}
 * @returns {Promise<*|null|{success: boolean}>}
 */
export async function /**/ do_rpc_query(
    type,
    table,
    params = null,
    funcGetDataStructure = null,
    funcGetGridApi = null,
    schemaName = null
) {
    let requestData;
    // проверяем корректность входных параметров
    const availableTypes = [
        'read',
        'read_template',
        'modify',
        'savedraft',
        'save_template',
        'delete',
        'restore',
        'login',
        'read_structure',
        'read_any', // читает любые неудаленные данные
    ];
    if (!type || !availableTypes.includes(type)) {
        console.log('do_rpc_query: INCORRECT TYPE OF QUERY!');
        return null;
    }
    if (!table || typeof table !== 'string' || table.length < 1) {
        console.log('do_rpc_query: INCORRECT TABLE NAME!');
        return null;
    }
    if (type !== 'read' && type !== 'read_structure' && (!params || typeof params !== 'object')) {
        console.log('do_rpc_query: INCORRECT PARAMS!');
        return null;
    }

    const dataStructure = funcGetDataStructure ? funcGetDataStructure() : null;

    let rpc_params;
    if (type === 'read' || type === 'read_template' || type === 'read_any') {
        rpc_params = {
            schemaName: dataStructure ? dataStructure.schema : config.currentSchema,
            tableName: table,
            sqlFilter: params?.sqlFilter || '',
            tableStructure: !!params && !!params.tableStructure ? params.tableStructure : null,
            _legacyJson: params?._legacyJson,
        };
        if (params && params.getDocumentAccessRight) {
            rpc_params.getDocumentAccessRight = true;
        }
    } else if (type === 'read_structure') {
        rpc_params = {
            schemaName: dataStructure ? dataStructure.schema : config.currentSchema,
            tableName: table,
        };
    } else if (type === 'login') {
        rpc_params = params;
    } else {
        if (!Array.isArray(params)) {
            if (!Object.keys(params)?.length) {
                console.log('do_rpc_query: warning - no data to save');
                return { success: true };
            }
        } else {
            if (params.length === 0) {
                console.log('do_rpc_query: warning - no data to save');
                return { success: true };
            }
        }

        rpc_params = {
            schemaName: dataStructure ? dataStructure.schema : config.currentSchema,
            tableName: table,
            data: !Array.isArray(params) ? [params] : params,
        };
    }

    if (schemaName) {
        rpc_params.schemaName = schemaName;
    }
    if (params?.page) rpc_params.page = params?.page;

    // let numeratorField = [],
    //     numeratorFieldNames = [],
    //     numeratorPrefix,
    //     prefixData;
    // if (type === 'modify' && (funcGetGridApi || dataStructure)) {
    //     let columnDefs = dataStructure?.structure
    //         ? dataStructure.structure
    //         : funcGetGridApi?.columnController?.columnDefs
    //         ? funcGetGridApi.columnController.columnDefs
    //         : [];
    //     // let columnDefs = funcGetGridApi ? funcGetGridApi.columnController.columnDefs : dataStructure.structure;
    //     columnDefs.forEach((column) => {
    //         if (column.columnType === 'NUMERATOR') {
    //             const numeratorColumn = {
    //                 name: column.field,
    //                 count: 0,
    //             };
    //             if (!!column.numeratorPrefix && !numeratorPrefix) numeratorPrefix = column.numeratorPrefix;
    //             numeratorFieldNames.push(column.field);
    //             numeratorField.push(numeratorColumn);
    //         }
    //     });
    // }

    // let numeratorPrefixArray = {};
    // let isAnyNumberChanged = false;
    if (type === 'modify' || type === 'savedraft' || type === 'save_template') {
        let rowItem;
        if (dataStructure) {
            let newItem;
            const dataByStructure = [];
            for (rowItem of rpc_params.data) {
                if (!!dataStructure.parentFieldName && !rowItem[dataStructure.parentFieldName] && !!rowItem.parent_id) {
                    if (typeof rowItem.parent_id === 'object') {
                        rowItem[dataStructure.parentFieldName] = rowItem.parent_id ? rowItem.parent_id.value : null;
                    } else {
                        rowItem[dataStructure.parentFieldName] = rowItem.parent_id;
                    }
                }

                if (dataStructure?.structure?.length) {
                    dataStructure.structure = dataStructure.structure.filter(
                        (itemStructure) => itemStructure?.field !== 'sys_shared'
                    );
                }

                newItem = {};
                if (rowItem.__global__ === true) {
                    newItem.__global__ = true;
                }
                dataStructure.structure.forEach(function (itemStructure) {
                    if (!!itemStructure.field && !!rowItem.hasOwnProperty(itemStructure.field)) {
                        if (rowItem[itemStructure.field] == null) {
                            newItem[itemStructure.field] = null; //для NULL и UNDEFINED - поле должно быть заполнено, иначе в БД он не сохранится (если undefined)
                            // else if (typeof rowItem[itemStructure.field] === "object") {
                            //         newItem[itemStructure.field] = (!!rowItem[itemStructure.field] ? rowItem[itemStructure.field].value : null);
                            //     }
                        } else if (
                            !!itemStructure.columnType &&
                            (itemStructure.columnType === 'REFERENCE_MULTI_SELECT' ||
                                itemStructure.columnType === 'REFERENCE_SELECT_JSON')
                        ) {
                            if (typeof rowItem[itemStructure.field] !== 'string') {
                                newItem[itemStructure.field] = JSON.stringify(rowItem[itemStructure.field]);
                            } else {
                                newItem[itemStructure.field] = rowItem[itemStructure.field];
                            }
                        } else {
                            newItem[itemStructure.field] = rowItem[itemStructure.field];
                        }
                    }

                    //Заполнение полей {ORG} и {PROJECT} для шаблона NUMERATOR
                    // if (
                    //     (rowItem.sys_row_status === 'ACT' || rowItem.sys_row_status === 'NEW') &&
                    //     numeratorField?.length &&
                    //     numeratorPrefix
                    // ) {
                    //     for (let prefix in numeratorPrefix) {
                    //         if (numeratorPrefix[prefix] === itemStructure.field) {
                    //             if (!!rowItem[itemStructure.field] && rowItem[itemStructure.field].value) {
                    //                 if (!numeratorPrefixArray[prefix]) {
                    //                     numeratorPrefixArray[prefix] = [rowItem[itemStructure.field].value];
                    //                 } else {
                    //                     numeratorPrefixArray[prefix].push(rowItem[itemStructure.field].value);
                    //                 }
                    //             } else if (
                    //                 !!itemStructure.columnType &&
                    //                 itemStructure.columnType === 'REFERENCE_MULTI_SELECT'
                    //             ) {
                    //                 let fieldValueMultiSelect = extFunc.getArrayFromJson(rowItem[itemStructure.field]);
                    //                 if (!!fieldValueMultiSelect && fieldValueMultiSelect.length > 0) {
                    //                     if (!numeratorPrefixArray[prefix]) {
                    //                         numeratorPrefixArray[prefix] = [fieldValueMultiSelect[0].value];
                    //                     } else {
                    //                         numeratorPrefixArray[prefix].push(fieldValueMultiSelect[0].value);
                    //                     }
                    //                 }
                    //             }
                    //         }
                    //     }
                    // }
                    // if (
                    //     (rowItem.sys_row_status === 'ACT' || rowItem.sys_row_status === 'NEW') &&
                    //     numeratorField?.length &&
                    //     numeratorFieldNames.includes(itemStructure.field)
                    // ) {
                    //     numeratorField.forEach((numerator) => {
                    //         if (numerator.name === itemStructure.field) {
                    //             if (rowItem[itemStructure.field]) {
                    //                 newItem[itemStructure.field] = rowItem[itemStructure.field];
                    //             } else {
                    //                 numerator.count++;
                    //                 isAnyNumberChanged = true;
                    //             }
                    //         }
                    //     });
                    // }
                });
                if (Object.keys(newItem).length > 0) {
                    dataByStructure.push(newItem);
                }
            }
            rpc_params.data = dataByStructure.slice();

            // if (
            //     type === 'modify' &&
            //     numeratorField?.length &&
            //     isAnyNumberChanged &&
            //     !!numeratorPrefixArray &&
            //     !_.isEmpty(numeratorPrefixArray)
            // ) {
            //     const prefixParams = {
            //         schemaName: 'platform',
            //         tableName: "get_prefix_for_numerator('" + JSON.stringify(numeratorPrefixArray) + "'::json)",
            //         sqlFilter: "_main_table_.sys_row_status = 'ACT'",
            //         tableStructure: [
            //             getTableStructureItem('platform', 'numerator_prefix', 'id', 'uuid'),
            //             getTableStructureItem('platform', 'numerator_prefix', 'prefix', 'character varying'),
            //         ],
            //     };
            //     prefixData = await do_rpc('read', prefixParams).then((response) => {
            //         requestData = response ? response.data : null;
            //         return requestData;
            //     });
            // }
        } else {
            for (rowItem of rpc_params.data) {
                if (!rowItem) continue;
                for (const prop in rowItem) {
                    if (rowItem[prop] == null) {
                        rowItem[prop] = null; //для NULL и UNDEFINED - поле должно быть заполнено, иначе в БД он не сохранится (если undefined)
                    }
                    // else if (typeof rowItem[prop] === "object") {
                    //     rowItem[prop] = (!!rowItem[prop] ? rowItem[prop].value : null);
                    // }
                }
            }
        }
    }

    let do_rpc_type;
    if (type === 'modify') {
        await setParams(rpc_params.data, 'ACT', type);
        do_rpc_type = 'modify';
    } else if (type === 'savedraft') {
        await setParams(rpc_params.data, 'DRF', type);
        do_rpc_type = 'modify';
    } else if (type === 'save_template') {
        await setParams(rpc_params.data, 'TMP', type);
        do_rpc_type = 'modify';
    } else if (type === 'delete') {
        await setParams(rpc_params.data, 'DEL', type);
        do_rpc_type = 'delete';
    } else if (type === 'restore') {
        await setParams(rpc_params.data, 'ACT', type);
        do_rpc_type = 'modify';
    } else if (type === 'read' || type === 'read_template' || type === 'read_any') {
        const sqlFilter =
            type === 'read'
                ? ["main_table.sys_row_status = 'ACT'"]
                : type === 'read_template'
                  ? ["main_table.sys_row_status = 'TMP'"]
                  : ["main_table.sys_row_status != 'DEL'"];
        rpc_params.sqlFilter = [sqlFilter, ...sqlFilterAnyProperty(rpc_params.sqlFilter)];
        do_rpc_type = 'read';
    } else {
        do_rpc_type = type;
    }
    const data = await do_rpc(do_rpc_type, rpc_params).then((response) => {
        requestData = response;
        return requestData;
    });

    if (data?.success && (funcGetGridApi || dataStructure) && do_rpc_type === 'modify') {
        if (type === 'delete' || type === 'restore') {
            gridTransactionToNull(funcGetGridApi, rpc_params.data);
        }
        // else if (numeratorField?.length && isAnyNumberChanged) {
        //     let secondSave = false;
        //     const numeratorData = await rpc_numerator('read', rpc_params.schemaName, table, null);
        //     if (numeratorData?.length) {
        //         let numeratorChanged = false;
        //         numeratorData.forEach((numberFromTable) => {
        //             numeratorField.forEach((numerator) => {
        //                 if (
        //                     numberFromTable.sys_metadata_field_id &&
        //                     numerator.name === numberFromTable.sys_metadata_field_id.name
        //                 ) {
        //                     numerator.current = numberFromTable.number_current;
        //                     numberFromTable.number_current += numerator.count;
        //                     numerator.number_template = numberFromTable.number_template;
        //                     numerator.number_length = numberFromTable.number_length;
        //                     numerator.number_fill = numberFromTable.number_fill;
        //                     numeratorChanged = numeratorChanged || numerator.count > 0;
        //                 }
        //             });
        //         });
        //         if (numeratorChanged) await rpc_numerator('modify', null, null, numeratorData);
        //     }
        //
        //     for (let rowItem of rpc_params.data) {
        //         if (rowItem.sys_row_status !== 'ACT') continue;
        //         numeratorField.forEach((numerator) => {
        //             if (!rowItem[numerator.name] && numerator?.current) {
        //                 let currentNumber = numerator.current.toString();
        //                 if (!!numerator.number_length && !!numerator.number_fill) {
        //                     currentNumber = currentNumber.padStart(
        //                         numerator.number_length,
        //                         numerator.number_fill.toString()
        //                     );
        //                 } else if (numerator.number_length) {
        //                     currentNumber = currentNumber.padStart(numerator.number_length, '0');
        //                 }
        //                 if (numerator.number_template) {
        //                     let template = numerator.number_template;
        //                     template = template.replaceAll('{NUMBER}', currentNumber);
        //                     if (!!prefixData && !!prefixData.length) {
        //                         prefixData.forEach((pref) => {
        //                             for (let prefix in numeratorPrefix) {
        //                                 if (!numeratorPrefix[prefix]) continue;
        //                                 let fieldVal = rowItem[numeratorPrefix[prefix]];
        //                                 if (typeof fieldVal === 'string') fieldVal = JSON.parse(fieldVal);
        //                                 if (fieldVal?.value) {
        //                                     fieldVal = fieldVal.value;
        //                                 } else if (fieldVal?.length && fieldVal.length === 1) {
        //                                     fieldVal = fieldVal[0].value;
        //                                 }
        //                                 if (prefix === 'org' && fieldVal === pref.id) {
        //                                     template = template.replaceAll('{ORG}', pref.prefix);
        //                                 } else if (prefix === 'project' && fieldVal === pref.id) {
        //                                     template = template.replaceAll('{PROJECT}', pref.prefix);
        //                                 }
        //                             }
        //                         });
        //                     }
        //                     template = template.replaceAll('{ORG}', '');
        //                     template = template.replaceAll('{PROJECT}', '');
        //                     rowItem[numerator.name] = template;
        //                 } else {
        //                     rowItem[numerator.name] = currentNumber;
        //                 }
        //                 numerator.current++;
        //                 secondSave = true;
        //             }
        //         });
        //     }
        //
        //     gridTransactionToNull(funcGetGridApi, rpc_params.data, numeratorField);
        //     if (secondSave) {
        //         data = await do_rpc('modify', rpc_params).then((response) => {
        //             requestData = response;
        //             return requestData;
        //         });
        //     }
        // }

        //возвращаем данные которые были отправлены на сервер
        data.sentData = rpc_params.data ? rpc_params.data : [];
        if (data.data?.length) {
            for (const item of data.data) {
                const idx = data.sentData.findIndex((row) => row.id === item.id);
                if (idx >= 0) {
                    data.sentData[idx] = { ...data.sentData[idx], ...item };
                } else {
                    data.sentData.push(item);
                }
            }
        }
    }

    if (data) {
        if (do_rpc_type === 'read') {
            return data.data;
        } else {
            return data;
        }
    }
    return null;
}

export async function wrapPlatformRequest(saveFn, data, funcGetGridApi) {
    if (data?.length > 0) {
        data.forEach((item) => {
            if (item.sys_row_status === 'NEW') {
                item.sys_row_status = 'ACT';
            }
            for (const prop in item) {
                if (item[prop] == null) {
                    item[prop] = null; //для NULL и UNDEFINED - поле должно быть заполнено, иначе в БД он не сохранится (если undefined)
                } else if (typeof item[prop] === 'object') {
                    item[prop] = item[prop] ? item[prop].value : null;
                }
            }
        });
    }
    return saveFn().then((saveResult) => {
        if (data) {
            gridTransactionToNull(funcGetGridApi, data);
        }
        return saveResult;
    });
}

export async function do_rpc_rpc_send_email(data) {
    let requestData;

    if (data == null)
        return {
            success: false,
            error: 'do_rpc_sendemail -> Empty params',
        };
    const type = 'sendemail';
    data.token = 'PUSK';
    data.apiKey = config.apiKeyFileStore;
    data.sysParams = UserStore.userState.sysParams;

    return await axios
        .post(`${config.api_url}/${type}`, data)
        .then((response) => {
            if (!!response && response.status === 200 && !!response.data && !!response.data.success) {
                requestData = response.data;
                console.debug('do_rpc_sendemail - OK 200');
                console.debug(response);
                return requestData;
            } else {
                console.error('do_rpc_sendemail - NOT OK', response);
                throw new Error('Error');
            }
        })
        .catch((error) => {
            console.error('do_rpc_sendemail - ERROR: ' + error);
            requestData = {
                success: false,
                error: 'do_rpc_sendemail -> ERROR: ' + error,
            };
            return requestData;
        });
}

/**
 * @return Object {
 *   success - true/false (есть ошибка или нет)
 *   error - текст ошибки
 * }
 * @param subject - тема письма
 * @param message - текст письма
 * @param emails - список почт через запятую
 * @param parents - массив id записей, для отправки файлов этих строк грида
 * @param emailConfigId - id почты отправителя из email_config
 */
export async function rpc_send_email(subject, message, emails, parents, emailConfigId) {
    const data = {
        subject: subject,
        message: message ? message : '',
        emails: emails,
        parents: parents,
        // mailserver: { //ВСЕ параметры должны быть STRING !!!
        //     host: 'smtp.gmail.com',
        //     ssl: 'true',
        //     tls: 'true',
        //     port: '465',
        //     auth: 'true',
        //     name: 'gp@ipgro.com',
        //     email: 'gp@ipgro.com',
        //     login: 'gp@ipgro.com',
        //     password: '777Asdfgh'
        // }
        mailserver: {
            //ВСЕ параметры должны быть STRING !!!
            // host: 'smtp.mail.ru',
            // ssl: 'true',
            // //tls: 'true',
            // port: '465',
            // auth: 'true',
            // name: 'ООО "Ай Пи Групп"',
            // email: 'gp.ipgro@mail.ru',
            // login: 'gp.ipgro@mail.ru',
            // password: '777Asdfgh',
            email_config_id: emailConfigId,
        },
    };

    const result = await do_rpc_rpc_send_email(data).then((response) => {
        return response;
    });

    return result;
}

export async function do_rpc_get_data(type, data) {
    let requestData;

    if (data == null)
        return {
            success: false,
            error: 'do_rpc_get_data -> Empty params',
        };
    data.sysParams = UserStore.userState.sysParams;

    return await axios
        .post(`${config.api_url}/${type}`, data)
        .then((response) => {
            if (!!response && response.status === 200 && !!response.data) {
                requestData = response.data;
                console.debug('do_rpc_get_data - OK 200');
                return requestData;
            } else {
                console.error('do_rpc_get_data - NOT OK');
                console.log(response);
                throw new Error('Error');
            }
        })
        .catch((error) => {
            console.error('do_rpc_get_data - ERROR: ' + error);
            requestData = {
                success: false,
                error: 'do_rpc_get_data -> ERROR: ' + error,
            };
            return requestData;
        });
}

/**
 * Чтение данных из DaData с фильтрацией по query
 * @return Object {
 *   success - true/false (есть ошибка или нет)
 *   error - текст ошибки
 * }
 * @param type - имя метода для чтения данных из DaData
 * @param query - значение поля
 */
export async function rpc_get_daData(type, query) {
    const data = {
        query: query,
    };
    const result = await do_rpc_get_data(type, data).then((response) => {
        return response;
    });

    return result;
}

/**
 * Чтение данных из CHILD-таблицы связанной через LINK Таблицу в виде списка объектов {value: ?, name: ?, label: ?}
 * @return Object {
 *   success - true/false (есть ошибка или нет)
 *   error - текст ошибки
 * }
 * @param tableNameChild - имя CHILD-таблицы откуда читаем данные
 * @param tableNameLink - имя LINK-таблицы в которой лежат связки ID<->ID
 * @param fieldNameLinkParent - имя поля в LINK-таблице в которой находится parentId
 * @param fieldNameLinkChild - имя поля в LINK-таблице в которой находится childId
 * @param parentId - ID из parent-таблицы
 * @param schemaName - имя схемы для таблиц (по умолчанию берет из настроек)
 * @param childSchemaName - имя схемы для CHILD-таблицы (по умолчанию берет из настроек)
 * @param fieldNameSortChild - имя поля в CHILD-таблице по которому будут сортированы данные (с указанием asc или desc. Например: 'date desc'). ТОЛЬКО ОДНО ПОЛЕ.
 * @param additionalParams.additionalFieldsMainTable - Массив с названиями полей которые, нужно взять из ОСНОВНОЙ таблицы
 * @param additionalParams.isCheckType - Делать ли в выборке проверку на тип поля TC(total count)
 */
export async function rpc_get_linkedData(
    tableNameChild,
    tableNameLink,
    fieldNameLinkParent,
    fieldNameLinkChild,
    parentId,
    schemaName,
    childSchemaName,
    fieldNameSortChild,
    additionalParams = null
) {
    if (!tableNameChild || !tableNameLink || !fieldNameLinkParent || !fieldNameLinkChild || !parentId) {
        console.error('rpc_get_linkedData - ERROR: Some parameters are missing');
        return {
            success: false,
            error: 'rpc_get_linkedData - ERROR: Some parameters are missing',
        };
    }
    const data = {
        schemaName: schemaName ? schemaName : config.currentSchema,
        tableNameChild: tableNameChild,
        tableNameLink: tableNameLink,
        fieldNameLinkParent: fieldNameLinkParent,
        fieldNameLinkChild: fieldNameLinkChild,
        parentId: parentId,
    };

    if (childSchemaName != null) {
        data.childSchemaName = childSchemaName;
    }

    if (fieldNameSortChild != null) {
        data.fieldNameSortChild = fieldNameSortChild;
    }

    if (additionalParams) {
        data.isCheckType = !!additionalParams.isCheckType;
        data.additionalFieldsMainTable = additionalParams.additionalFieldsMainTable;
    }

    return await NodeAPI.RpcService.readLinkedData({ body: data })
        .then((res) => ({ success: true, data: res }))
        .catch((error) => {
            console.error('do_rpc_get_data - ERROR: ' + error);
            return {
                success: false,
                error: 'do_rpc_get_data -> ERROR: ' + error,
            };
        });
}

const contentTypesMap = {
    'application/pdf': 'pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
};
/**
 * Формирование и скачивание печатной формы по шаблону
 * @param reportTemplate {Object} - параметра шаблона из platform.sys_report_template
 * @param fileName {String} - имя файла для сохранения
 * @param data {Object} - ARRAY of data для шаблона, обычно приходит в формате:
 * [{
 *   data: {...}, - объект Документа
 *   children: {
 *     data: [{...}, {...}, ...], - массив строк из Документа
 *   },
 *   additional: {
 *     ... - любые дополнительные данные
 *   }
 * }, {...}, ...]
 *
 * @param extension {'pdf' | 'docx'} - тип получаемой печатной формы
 * @returns {Promise<boolean|{success: boolean, error: string, data?: File}>}
 */
export async function rpc_get_report(reportTemplate, data, extension = 'pdf', fileName = null, save = true) {
    const result = {
        success: false,
        data: null,
        error: '',
    };
    if (!reportTemplate) {
        console.log('Не задан шаблон печатной формы', 'rpc_get_report');
        result.success = false;
        result.error = 'Не задан шаблон печатной формы';
        return result;
    }

    const reportData = {
        data: !data ? [{}] : data,
    };

    const sysParams = {
        idToken: localStorage.getItem(config.idToken),
        //FIXME Тут похоже должен быть "sysOrgId", а не "sysServiceOrg"
        sysServiceOrg: UserStore.userState.sysParams.sysOrgId || null,
        ...UserStore.userState.sysParams,
    };
    reportData.apiKey = config.apiKeyFileStore;

    reportData.reportTemplate = reportTemplate; //для доступа к параметрам шаблона на серверной стороне и в печатной форме

    await platformAxios
        .post(
            `/document-generation`,
            { ...reportData, sysParams, extension },
            {
                responseType: 'blob',
            }
        )
        .then((response) => {
            const ext = contentTypesMap[response.headers['content-type']];
            console.debug(response, 'OK - rpc_get_report');
            let saveFileName = (fileName ?? reportTemplate.name) + '.' + ext;
            saveFileName = saveFileName.replace(/["']/g, ' ');
            saveFileName = saveFileName.replace(/[/\\?%*:|<>]/g, '_');
            saveFileName = saveFileName ? saveFileName : 'report.' + ext;
            if (save) {
                saveAs(new Blob([response.data]), saveFileName);
            } else {
                result.data = new File([response.data], saveFileName, {
                    type: response.headers['content-type'],
                });
            }
            result.success = true;
        })
        .catch(async function (error) {
            console.warn(error, 'ERROR - rpc_get_report');
            result.success = false;
            result.error = error.response?.data ? await new Response(error.response.data).text() : 'Шаблон не загружен';
        });

    return result;
}

/**
 * Формирование и скачивание печатной формы по шаблону
 * @param ids {Object} - параметра шаблона из platform.sys_report_template
 * @returns {Promise<boolean|{success: boolean, error: string}>}
 */
export async function rpc_document_create_based_on(ids, documentBaseType, documentCreatedType, saveDoc = false) {
    const result = {
        success: false,
        error: '',
    };
    ids = typeof ids === 'string' ? [ids] : ids;
    if (!ids || !documentBaseType || !documentCreatedType || !Array.isArray(ids)) {
        result.success = false;
        result.error = 'Не задан параметры создания Документа';
        console.log(result.error, 'rpc_document_create_based_on');
        return result;
    }

    const sysParams = {
        ...UserStore.userState.sysParams,
        idToken: localStorage.getItem(config.idToken),
    };
    await axios
        .post(`${config.apiUrlFastify}/document-create-based-on`, {
            sysParams: sysParams,
            documentBaseType: documentBaseType,
            documentCreatedType: documentCreatedType,
            authorEmployee: UserStore.userState.user?.currentEmployeeId,
            data: ids,
            saveDoc: saveDoc,
        })
        .then((response) => {
            console.log(response, 'OK - rpc_document_create_based_on');
            if (!response?.data?.success) {
                result.success = false;
                result.error = response?.data?.error;
            } else {
                result.success = true;
                result.data = response?.data?.data;
            }
        })
        .catch(async function (error) {
            console.log(error, 'ERROR - rpc_document_create_based_on');
            result.success = false;
            result.error = error.response?.data ? await new Response(error.response.data).text() : 'Документ не создан';
        });

    return result;
}

/**
 * Запрос к методу обработки ДЕЙСТВИИ (ACTION) для Документооборота
 * @param docflowSubject {String} - субъект Документооброта (platform.docflow_subject)
 * @param data {Object} - data для действия
 * @returns {Promise<{data: object, success: true}>}
 */
export async function rpc_fastify_action_query(docflowSubject, data) {
    if (!docflowSubject) {
        console.log('Не задан субъект Документооброта (platform.docflow_subject)', 'rpc_fastify_action_query');
        throw new Error('Не задан субъект Документооброта (platform.docflow_subject)');
    }

    data = !data ? {} : data;

    data.sysParams = {
        idToken: localStorage.getItem(config.idToken),
        ...UserStore.userState.sysParams,
    };

    return axios
        .post(`${config.apiUrlFastify}/actions/${docflowSubject}`, data)
        .then((response) => {
            console.debug(response, 'OK - rpc_fastify_query');
            return { data: response.data?.result, success: true };
        })
        .catch((error) => {
            console.error(error?.response, 'ERROR - rpc_fastify_query');
            return Promise.reject(new Error(error.response?.data?.message));
        });
}

// /**
//  * Чтение и запись данных из numerator для нумерации в таблицах
//  * @return Object {
//  *   success - true/false (есть ошибка или нет)
//  *   error - текст ошибки
//  * }
//  * @param type - тип чтения данных из таблицы
//  * @param schemaName - название схемы в БД у таблицы, для которой читать из таблицы numerator
//  * @param tableName - название таблицы, для которой читать из таблицы numerator
//  * @param data - дата для записи в таблицу
//  */
// export async function rpc_numerator(type, schemaName, tableName, data) {
//     const availableTypes = ['read', 'modify'];
//     if (!type || !availableTypes.includes(type)) {
//         console.log('rpc_numerator: INCORRECT TYPE OF QUERY!');
//         return null;
//     }
//     let result;
//     if (!!data && type === 'modify') {
//         result = await do_rpc_query(type, 'numerator', data, null, null, 'platform').then((response) => {
//             return response;
//         });
//     } else {
//         const table_query = {
//             tableStructure: [
//                 getTableStructureItem('platform', 'numerator', 'id', 'uuid'),
//                 getTableStructureItem('platform', 'numerator', 'name', 'character varying'),
//                 getTableStructureItem('platform', 'numerator', 'description', 'character varying'),
//                 getTableStructureItem('platform', 'numerator', 'number_start', 'bigint'),
//                 getTableStructureItem('platform', 'numerator', 'number_current', 'bigint'),
//                 getTableStructureItem('platform', 'numerator', 'number_template', 'character varying'),
//                 getTableStructureItem('platform', 'numerator', 'number_length', 'bigint'),
//                 getTableStructureItem('platform', 'numerator', 'number_fill', 'character varying'),
//                 getTableStructureItem('platform', 'numerator', 'sys_metadata_id', 'uuid'),
//                 getTableStructureItem('platform', 'numerator', 'sys_metadata_field_id', 'uuid'),
//                 getTableStructureItem('platform', 'sys_metadata', 'name', 'character varying', 'sys_metadata_id', 1),
//                 getTableStructureItem(
//                     'platform',
//                     'sys_metadata_field',
//                     'field_name',
//                     'character varying',
//                     'sys_metadata_field_id',
//                     2
//                 ),
//             ],
//         };
//         result = await do_rpc_query(
//             type,
//             `get_numerator_params('${config.serviceId}'::uuid, '${UserStore.userState.sysParams.sysOrgId}'::uuid, '${schemaName}'::varchar, '${tableName}'::varchar)`,
//             table_query,
//             null,
//             null,
//             'platform'
//         ).then((response) => {
//             return response;
//         });
//     }
//
//     return result;
// }

/**
 * Возвращает элемент описывающий структуру колонки БД
 * @param schemaName {string} - схема
 * @param tableName {string} - таблица
 * @param columnName {string} - имя колонки
 * @param dataType {string} - тип данных
 * @param linkColumnName {string|null} - имя связанной колонки
 * @param sort {number|null} - сортировка для последовательных JOIN'ов
 * @param dataTypeKeys {string|null} - ТОЛЬКО ДЛЯ COMPOSITE TYPES, перечень ключей в типе (например: "is_child,sys_org_integration_id,link")
 * @returns {{dataTypeKeys: (null|string), linkColumnName (null|string), sqlPart: (string), dataType (string), sort: (number), tableSchema: (string), tableName (string), columnName (string)}}
 */
export function getTableStructureItem(
    schemaName,
    tableName,
    columnName,
    dataType,
    linkColumnName = null,
    sort = null,
    dataTypeKeys = null
) {
    return {
        tableSchema: schemaName ? schemaName : config.currentSchema,
        tableName: tableName,
        columnName: columnName,
        dataType: dataType,
        linkColumnName: linkColumnName,
        sqlPart: !linkColumnName ? '_main_table_' : '_join_table_' + sort + '_',
        sort: !sort ? 0 : sort,
        dataTypeKeys: dataTypeKeys,
    };
}

// Заполнение GUID и SYS_ROW_STATUS
async function setParams(dataArray, sysRowStatus, type) {
    if (!dataArray || dataArray.length < 1) {
        return null;
    }
    let row;
    for (let i = 0; i < dataArray.length; i++) {
        row = dataArray[i];
        if (!row.id) {
            row.id = guid().toLocaleLowerCase();
        } else {
            row.id = row.id.toLocaleLowerCase();
        }
        row.sys_row_status = !row.sys_row_status ? sysRowStatus : type === 'modify' ? row.sys_row_status : sysRowStatus;
        // row.sys_row_status = row.sys_row_status === 'NEW' ? 'ACT' : row.sys_row_status;
        if (type === 'delete' || type === 'restore') {
            dataArray[i] = {
                id: row.id,
                sys_row_status: row.sys_row_status,
            };
        }
    }
}

function isUUID(uuid) {
    if (!uuid) {
        return false;
    }
    let s = '' + uuid;
    s = s.toLocaleLowerCase();

    return !(
        s.match('^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$') === null ||
        s.match('^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$') === null
    );
}

export function gridTransactionToNull(funcGetGridApi, data, numberFields = undefined) {
    if (!data) return;

    if (!funcGetGridApi) {
        data.forEach(function (item) {
            if (item?.sys_row_status === 'NEW') {
                item.sys_row_status = 'ACT';
            }
        });
    } else {
        let gridApi;
        if (typeof funcGetGridApi === 'function') {
            gridApi = funcGetGridApi();
        } else {
            gridApi = funcGetGridApi;
        }

        const updateData = [];
        gridApi.forEachNode(function (node) {
            if (!!node.data && !!node.data.gridTransactionId) {
                if (node.data.sys_row_status === 'NEW') {
                    node.data.sys_row_status = 'ACT';
                }
                const foundIndex = data.findIndex((row) => row.id === node.data.id);
                if (foundIndex >= 0) {
                    node.data.gridTransactionId = null;
                    if (numberFields && node.data.sys_row_status !== 'DEL') {
                        numberFields.forEach((numberField) => {
                            node.data[numberField.name] = data[foundIndex][numberField.name];
                        });
                    }
                }
                updateData.push(node.data);
            }
        });

        gridApi.applyTransaction({ update: updateData });
    }
}

/**
 * Загрузка данных из файла (разных форматов)
 * @param file {Blob} - файл
 * @param params {Object} - параметры загрузки
 *   {
 *      fileType: {String} invoice | act | operation
 *      docType: {String} тип документа для invoice | act
 *      project: {Object} проект для invoice | act
 *      authorEmployee: {Object} автор,
 *   }
 * @returns {Promise<{data: null, success: boolean, error: string}>}
 */
export async function rpc_upload_docs_from_file(file, params = null) {
    const result = {
        success: false,
        data: null,
        error: '',
    };

    const headers = { 'Content-Type': 'multipart/form-data' };
    const data = new FormData();

    data.append('sysOrgId', UserStore.userState.sysParams.sysOrgId);
    data.append('idToken', localStorage.getItem(config.idToken));
    data.append('sysServiceOrg', UserStore.userState.sysParams.sysOrgId);
    data.append('sysServiceId', config.serviceId);
    data.append('sysWithoutOrgHierarchy', `${UserStore.userState.sysParams.sysWithoutOrgHierarchy}`);
    data.append('file', file);
    data.append('fileType', params?.fileType); //тип XML-файла: 'invoice' или 'act' (пока только 2 типа)
    data.append('docType', params?.docType); //зависит от документа
    data.append('projectId', params?.project?.value);
    data.append('projectName', params?.project?.name);
    data.append('authorEmployeeId', params?.authorEmployee?.value);
    data.append('authorEmployeeName', params?.authorEmployee?.name);
    await axios
        .post(`${config.apiUrlFastify}/generation-docs`, data, {
            headers,
        })
        .then((response) => {
            console.debug(response, 'OK - generation-docs');
            if (!response?.data?.success) {
                result.success = false;
                result.error = response?.data?.error;
            } else {
                result.success = true;
                result.data = response?.data?.data;
            }
        })
        .catch(async function (error) {
            console.log(error, 'ERROR - generation-docs');
            result.success = false;
            result.error = error.response?.data ? await new Response(error.response.data).text() : 'Файл не загружен';
        });

    return result;
}

/**
 * Возвращает employee_id по текущему авторизованному пользователю
 * @deprecated Уже не надо делать запрос, данные есть в объекте пользователя
 * @returns {Promise<null|Object>}
 */
export async function funcGetCurrentEmployeeId(personId = UserStore.userState.user?.person_id) {
    const person_id = normalize(personId);
    if (!person_id) {
        return null;
    }
    const now = new Date().getTime() / 1000;
    const data = await NodeAPI.Rpc.readWithRefs(EmployeeEntity, {
        filters: [
            sqlFilter`main_table.person_id = ${person_id}`,
            {
                op: 'OR',
                children: [
                    sqlFilter`main_table.period_start is NULL`,
                    sqlFilter`main_table.period_start <= to_timestamp(${now})`,
                ],
            },
            {
                op: 'OR',
                children: [
                    sqlFilter`main_table.period_end is NULL`,
                    sqlFilter`main_table.period_end >= to_timestamp(${now})`,
                ],
            },
        ],
    });
    //читаем записи из employee по рабочим местам person_id на текущий день
    if (!data || data.length === 0) {
        console.warn('Текущий пользователь не привязан к действующему сотруднику!');
        return null;
    } else if (data.length === 1) {
        // если одно рабочее место
        return extFunc.getIdItem(data[0].id, data[0].name);
    }

    // если рабочих мест несколько, то ищем первую "основную должность"
    for (const item of data) {
        if (item.main) return extFunc.getIdItem(item.id, item.name);
    }

    console.warn('Текущий пользователь не привязан к действующему сотруднику по "основной" должности!');
    return null;
}

/**
 * Возвращает список string employee_project_id по текущему авторизованному пользователю
 * @returns {Promise<null|Object>}
 */
export async function funcGetCurrentEmployeeProjectsId(employee_id) {
    const employeeId = normalize(employee_id);
    if (!employeeId) {
        return null;
    }

    const data = await NodeAPI.Rpc.readWithRefs(EmployeeProjectEntity, {
        filters: [sqlFilter`main_table.employee_id = ${employeeId}`],
        joins: [
            {
                ref: 'platform.project',
                alias: '_p_',
                on: "main_table.project_id = _p_.id and _p_.sys_row_status = 'ACT'",
                include: [['sys_doc_state', 'project_sys_doc_state']],
            },
        ],
    });

    return data
        ?.filter((item) => item?.project_sys_doc_state === statuses.PROJECT.approved.value)
        ?.map?.((item) => normalize(item?.project_id));
}

export async function funcGetCurrentSysDocStateSettingByUserRolesIdListIn(userRolesId, serviceId, sysOrgId) {
    const table_query = {
        tableStructure: [
            getTableStructureItem('platform', 'sys_doc_state_setting', 'id', 'uuid'),
            getTableStructureItem('platform', 'sys_doc_state_setting', 'sys_row_status', 'character varying'),
            getTableStructureItem('platform', 'sys_doc_state_setting', 'name', 'character varying'),
            getTableStructureItem('platform', 'sys_doc_state_setting', 'sys_service_org_link_id', 'uuid'),
            getTableStructureItem('platform', 'sys_doc_state_setting', 'sys_doc_state_from', 'character varying'),
            getTableStructureItem('platform', 'sys_doc_state_setting', 'sys_doc_state_in', 'json'),
            getTableStructureItem('platform', 'sys_doc_state_setting', 'sys_metadata_id', 'uuid'),
            getTableStructureItem('platform', 'sys_doc_state_setting', 'section', 'character varying'),
            getTableStructureItem('platform', 'sys_doc_state_setting', 'table_schema', 'character varying'),
            getTableStructureItem('platform', 'sys_doc_state_setting', 'table_name', 'character varying'),
            getTableStructureItem('platform', 'sys_doc_state_setting', 'sys_metadata_field_setting', 'json'),
            getTableStructureItem('platform', 'sys_doc_state_setting', 'sys_crud_function_setting', 'json'),
        ],
    };

    try {
        return await do_rpc_query(
            'read',
            `sys_get_current_sys_doc_state_setting_by_user_roles_id_list_in('${serviceId}'::uuid, '${sysOrgId}'::uuid,ARRAY [${userRolesId}]::uuid[])`,
            table_query,
            null,
            null,
            'platform'
        );
    } catch (e) {
        console.warn(e);
    }
}

export async function funcGetSysOption(serviceId, sysOrgId, sysUserId) {
    const table_query = {
        tableStructure: [
            getTableStructureItem('platform', 'sys_option', 'id', 'uuid'),
            getTableStructureItem('platform', 'sys_option', 'sys_row_status', 'character varying'),
            getTableStructureItem('platform', 'sys_option', 'sys_service_org_link_id', 'uuid'),
            getTableStructureItem('platform', 'sys_option', 'sys_metadata_id', 'uuid'),
            getTableStructureItem('platform', 'sys_option', 'sys_user_id', 'uuid'),
            getTableStructureItem('platform', 'sys_option', 'table_schema', 'character varying'),
            getTableStructureItem('platform', 'sys_option', 'table_name', 'character varying'),
            getTableStructureItem('platform', 'sys_option', 'option', 'character varying'),
            getTableStructureItem('platform', 'sys_metadata', 'name', 'character varying', 'sys_metadata_id', 1),
        ],
    };

    try {
        return await do_rpc_query(
            'read',
            `sys_get_option('${serviceId}'::uuid, '${sysOrgId}'::uuid, NULL::uuid, NULL::character varying, NULL::character varying, '${sysUserId}'::uuid)`,
            table_query,
            null,
            null,
            'platform'
        );
    } catch (e) {
        console.warn(e);
    }
}

/**
 * Возвращает информацию о организации по текущему авторизованному пользователю либо переданным employeeId
 * @returns {Promise<null|Object>}
 */
export async function funcGetInfoCurrentEmployeeId(employeeId) {
    employeeId = typeof employeeId === 'string' ? employeeId : employeeId?.value;

    if (!employeeId) {
        employeeId = await funcGetCurrentEmployeeId();
    }

    employeeId = typeof employeeId === 'string' ? employeeId : employeeId?.value;

    const table_query = {
        tableStructure: [
            getTableStructureItem('pusk', 'employee', 'id', 'uuid'),
            getTableStructureItem('pusk', 'employee', 'sys_row_status', 'character varying'),
            getTableStructureItem('pusk', 'employee', 'name', 'character varying'),
            getTableStructureItem('pusk', 'employee', 'description', 'character varying'),
            getTableStructureItem('pusk', 'employee', 'sys_service_org_link_id', 'uuid'),
            getTableStructureItem('pusk', 'employee', 'contragent_id', 'uuid'),
            getTableStructureItem('platform', 'contragent', 'name', 'character varying', 'contragent_id', 1),
            getTableStructureItem('pusk', 'commercial_offer', 'contragent_address', 'character varying'),
            getTableStructureItem('pusk', 'commercial_offer', 'contragent_kpp', 'character varying'),
            getTableStructureItem('pusk', 'commercial_offer', 'contragent_inn', 'character varying'),
            getTableStructureItem('pusk', 'commercial_offer', 'person_name', 'character varying'),
            getTableStructureItem('pusk', 'commercial_offer', 'person_name_full', 'character varying'),
            getTableStructureItem('pusk', 'commercial_offer', 'person_phone', 'character varying'),
            getTableStructureItem('pusk', 'commercial_offer', 'person_email', 'character varying'),
        ],
    };

    try {
        const queryResult = await do_rpc_query(
            'read',
            `get_contragent_and_person_by_employee_id('${employeeId}'::uuid)`,
            table_query,
            null,
            null,
            'pusk'
        );

        return queryResult?.[0];
    } catch (e) {
        console.warn(e);
        return;
    }
}
